import * as React from 'react'
import { useSessionUser } from '@/hooks/use-session'
import appCss from '../app.css?url'
import {
  ClientOnly,
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  retainSearchParams,
  stripSearchParams,
  useNavigate,
} from '@tanstack/react-router'
import { noop, useQueryClient, useSuspenseQuery, type QueryClient } from '@tanstack/react-query'
import { TooltipProvider } from '@/components/ui/tooltip'
import { Toaster } from '@/components/ui/sonner'
import { TanStackRouterDevtoolsPanel } from '@tanstack/react-router-devtools'
import { ReactQueryDevtoolsPanel } from '@tanstack/react-query-devtools'
import { TanStackDevtools } from '@tanstack/react-devtools'
import * as v from 'valibot'
import { FiltersShape, Profile } from '@/lib/profile-search-schema'
import { useAppStore } from '@/lib/app-store'
import { accentThemeVars } from '@/lib/accent-theme'
import { setCookie, deleteCookie } from '@/lib/cookies'
import { CookieBanner } from './_app/-components/shared/cookie-banner'
import { type I18n } from '@lingui/core'
import { I18nProvider } from '@lingui/react'
import { useLingui } from '@lingui/react/macro'
import { FormOptionalLabelProvider } from '@/components/custom/form/contexts'
import { resolveServerTheme } from '@/lib/server-theme'
import { HYDRATION_REWRITE_MONITOR_SCRIPT } from '@/lib/hydration-rewrite-monitor'
import { assetUrl, getRuntimeCdnUrl } from '@/lib/asset-url'
import type { Locale } from 'shared/locales'
import { persistedCacheIdentity } from '@/main-helpers'
import { trpc, clearQueryClient, OWNS_PERSISTED_CACHE, PERSISTED_IDENTITY_KEY } from '@/main'

// Mounted statically with the shell (sonner is ~9 KB gz in the entry): a toast must be interactive the moment the
// page is, not after an on-demand chunk fetch.
function AppToaster() {
  return (
    <Toaster
      className="whitespace-pre-line print:hidden"
      offset={{ top: 'calc(32px + env(safe-area-inset-top))' }}
      mobileOffset={{ top: 'calc(16px + env(safe-area-inset-top))' }}
      position="top-center"
      theme="light"
    />
  )
}

const defaultValues = {
  q: '',
  // The detail pane's OWN search, separate from `q`: two search boxes are visible at once on those screens.
  panelQ: '',
  p: 0,
  teamsSort: 'name',
  pageType: 'detailed',
  reportSort: 'index',
  distSort: 'index',
  distPosition: 'highs',
  boardType: 'competences',
  boardSort: 'index',
  boardAxis: 'x',
  orgType: 'basic',
  selected: 'first',
  type: 'competences',
  // TODO: this should not be necessary, check
  evaluation: 'owns',
} as const

const EvaluationV = v.picklist(['owns', 'ownsManual', 'team', 'manual'])

const rootSearchSchema = v.object({
  q: v.optional(v.string(), defaultValues.q),
  panelQ: v.optional(v.string(), defaultValues.panelQ),
  p: v.optional(v.pipe(v.number(), v.minValue(0)), defaultValues.p),
  o: v.fallback(v.optional(v.picklist(['adjust', 'global'])), undefined),
  od: v.fallback(v.optional(v.picklist(['asc', 'desc'])), undefined),
  offerSort: v.fallback(v.optional(v.picklist(['recent', 'applicants'])), undefined),
  jobSort: v.fallback(v.optional(v.picklist(['recent', 'applicants'])), undefined),
  teamsSort: v.fallback(v.optional(v.picklist(['name', 'size']), defaultValues.teamsSort), defaultValues.teamsSort),
  pageType: v.fallback(
    v.optional(v.picklist(['detailed', 'resume']), defaultValues['pageType']),
    defaultValues['pageType'],
  ),
  reportSort: v.fallback(
    v.optional(v.picklist(['index', 'desc']), defaultValues['reportSort']),
    defaultValues['reportSort'],
  ),
  distSort: v.fallback(v.optional(v.picklist(['desc', 'index']), defaultValues['distSort']), defaultValues['distSort']),
  distPosition: v.fallback(
    v.optional(v.picklist(['highs', /* 'medium', */ 'lows']), defaultValues['distPosition']),
    defaultValues['distPosition'],
  ),
  boardType: v.fallback(
    v.optional(v.picklist(['competences', 'vectors', 'dimensions']), defaultValues['boardType']),
    defaultValues['boardType'],
  ),
  boardSort: v.fallback(
    v.optional(v.picklist(['index', 'desc', 'asc']), defaultValues['boardSort']),
    defaultValues['boardSort'],
  ),
  boardId: v.optional(v.union([v.number(), v.string()])), // competence | vector | dimension | member
  boardAxis: v.fallback(v.optional(v.picklist(['x', 'y']), defaultValues['boardAxis']), defaultValues['boardAxis']),
  boardPosition: v.optional(v.number()),
  orgType: v.fallback(
    v.optional(v.picklist(['basic', 'professional', 'transversal', 'directives']), defaultValues['orgType']),
    defaultValues['orgType'],
  ),
  first: v.optional(Profile),
  second: v.optional(Profile),
  selected: v.fallback(
    v.optional(v.picklist(['first', 'second', 'compare']), defaultValues['selected']),
    defaultValues['selected'],
  ),
  // Additive and independent of `selected`/`first`/`second` (not in REPORT_INPUT_KEYS), so it never alters the
  // report query.
  collective: v.fallback(
    v.optional(v.picklist(['all', 'employees', 'jobs', 'teams', 'offers', 'candidates'])),
    undefined,
  ),
  // Kept in its OWN search key so the collective card's Role/Demographics refinements stay independent of the
  // compared profiles' filters; cleared whenever the active collective changes or closes.
  // The bounds are TAKEN from `FiltersShape`: without them a hand-edited URL parses here and is then rejected by the
  // tRPC input schema, which the card surfaces as a silent "No profiles found".
  collectiveFilters: v.optional(
    v.object({
      level: FiltersShape.level,
      function: FiltersShape.function,
      sector: FiltersShape.sector,
      peopleInCharge: FiltersShape.peopleInCharge,
      sex: FiltersShape.sex,
      ageMin: FiltersShape.ageMin,
      ageMax: FiltersShape.ageMax,
      educationalLevel: FiltersShape.educationalLevel,
    }),
  ),
  type: v.fallback(
    v.optional(v.picklist(['competences', 'values', 'personality']), defaultValues.type),
    defaultValues.type,
  ),
  // TODO: this should not be necessary, check
  teamId: v.optional(v.number()),
  evaluation: v.fallback(v.optional(EvaluationV), undefined),
  members: v.optional(v.array(v.number())),
  name: v.nullish(v.string()),
  // Form dialogs (opened via search params: number = edit by id, 'new' = create)
  editEmployee: v.optional(v.union([v.number(), v.literal('new')])),
  editTeam: v.optional(v.union([v.number(), v.literal('new')])),
  editOffer: v.optional(v.union([v.number(), v.literal('new')])),
  editJob: v.optional(v.union([v.number(), v.literal('new')])),
  // The ONLY URL-addressable way into the describe flow — the in-app entry points open it from local state — which is
  // what the public API's `GET /api/v1/jobs/:id/test` link points at. No 'new': describing needs an existing job.
  describeJob: v.optional(v.number()),
  // The public API hands out links like `/report?token=…&lang=…`; the token IS the session (sent as the Bearer header,
  // see main.tsx). Kept at the root + retained below so in-app navigation never strips the token out of the flow.
  token: v.optional(v.string()),
  lang: v.optional(v.string()),
  clientId: v.optional(v.string()),
  ref: v.optional(v.string()),
})

// Routes that retain all search params need this as the explicit type argument to `retainSearchParams<RootSearch>`:
// without it the generic falls back to `object`, which doesn't satisfy the route's search type.
export type RootSearch = v.InferOutput<typeof rootSearchSchema>

// Public per-deployment config from SST runtime secrets, which exist only server-side — the CI build (and so
// import.meta.env / Vite `define`) never sees them — hence process.env at SSR, baked in here. `process` is undefined
// in the browser, so the bundle ships the empty fallbacks and the `|| value` form makes the client a no-op clobber.
// USER/APP stay null by design: the SPA shell is auth-unaware and cacheable, so the user resolves client-side.
const publicEnv =
  typeof process !== 'undefined'
    ? {
        GOOGLE_CLIENT_ID: process.env.GOOGLE_CLIENT_ID ?? '',
        FACEBOOK_CLIENT_ID: process.env.FACEBOOK_CLIENT_ID ?? '',
        LINKEDIN_CLIENT_ID: process.env.LINKEDIN_CLIENT_ID ?? '',
        STRIPE_PUBLIC_KEY: process.env.STRIPE_PUBLIC_KEY ?? '',
        APP_ENV: process.env.APP_ENV ?? '',
        GA_MEASUREMENT_ID: process.env.GA_MEASUREMENT_ID ?? '',
        APP_VERSION: process.env.APP_VERSION ?? '',
        // Gates the client session-timeline capture (lib/session-actions); error reporting + breadcrumbs run regardless.
        WIDE_EVENTS_ENABLED: process.env.WIDE_EVENTS_ENABLED ?? '',
      }
    : null

const initGlobalsScript = [
  `window.GOOGLE_CLIENT_ID = window.GOOGLE_CLIENT_ID || ${JSON.stringify(publicEnv?.GOOGLE_CLIENT_ID ?? '')};`,
  `window.FACEBOOK_CLIENT_ID = window.FACEBOOK_CLIENT_ID || ${JSON.stringify(publicEnv?.FACEBOOK_CLIENT_ID ?? '')};`,
  `window.LINKEDIN_CLIENT_ID = window.LINKEDIN_CLIENT_ID || ${JSON.stringify(publicEnv?.LINKEDIN_CLIENT_ID ?? '')};`,
  `window.STRIPE_PUBLIC_KEY = window.STRIPE_PUBLIC_KEY || ${JSON.stringify(publicEnv?.STRIPE_PUBLIC_KEY ?? '')};`,
  `window.APP_ENV = window.APP_ENV || ${JSON.stringify(publicEnv?.APP_ENV ?? '')};`,
  `window.GA_MEASUREMENT_ID = window.GA_MEASUREMENT_ID || ${JSON.stringify(publicEnv?.GA_MEASUREMENT_ID ?? '')};`,
  `window.APP_VERSION = window.APP_VERSION || ${JSON.stringify(publicEnv?.APP_VERSION ?? '')};`,
  `window.WIDE_EVENTS_ENABLED = window.WIDE_EVENTS_ENABLED || ${JSON.stringify(publicEnv?.WIDE_EVENTS_ENABLED ?? '')};`,
  // `globalThis.__assetUrl` is the runtime resolver Vite's renderBuiltUrl form calls from JS chunks (vite.config.ts,
  // mirrored by lib/asset-url.ts). MUST be defined here, before <Scripts/>, so the first chunk's URLs already resolve.
  `window.__CDN_URL = window.__CDN_URL || ${JSON.stringify(getRuntimeCdnUrl())};`,
  `globalThis.__assetUrl = globalThis.__assetUrl || function(f){var b=(window.__CDN_URL||'').replace(/\\/$/,'');return (b?b+'/':'/')+String(f).replace(/^\\//,'')};`,
].join('')

// Anything but an explicit 'dark'/'light' — no cookie, or a stale 'theme=undefined' — means follow the OS preference;
// treating a non-light value as "not system" strips the class for system-dark visitors and flashes light→dark.
const manageThemeScript = `
function manageTheme() {
  var m = document.cookie.match(/(?:^|;\\s*)theme=([^;]*)/)
  var theme = m ? m[1] : ''
  var dark = theme === 'dark' || (theme !== 'light' && window.matchMedia('(prefers-color-scheme: dark)').matches)
  if (dark) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }
}
manageTheme()
window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', manageTheme)
`

// The render-critical font + favicon live in public/, so they bypass Vite and go through assetUrl(), which reads the
// same runtime CDN host as every other asset — SSR and the first client render then produce identical markup.
// The preconnect overlaps the CDN handshake with HTML parsing; cdnOrigin is '' for same-origin assets (dev / e2e).

export const Route = createRootRouteWithContext<{
  queryClient: QueryClient
  locale: Locale
  // Request-scoped so concurrent requests in one worker can't clobber each other's active locale (React #418).
  i18n: I18n
  // Set ONLY inside a route-transition live preview's shadow router (route-transition/route-preview.tsx): the root and
  // any transition host then render a bare <Outlet/>, so the pane holds the destination route, not a second shell.
  previewFrom?: string
}>()({
  validateSearch: rootSearchSchema,
  search: {
    middlewares: [retainSearchParams(['token', 'lang', 'clientId', 'ref']), stripSearchParams(defaultValues)],
  },
  context: () => ({
    sessionUserQueryOptions: trpc.auth.getUser.queryOptions(),
    sessionAppQueryOptions: trpc.auth.getApp.queryOptions(),
    myEmployersQueryOptions: trpc.employees.getMyEmployers.queryOptions(),
  }),
  loader: ({ context }) => {
    context.queryClient.query(context.sessionUserQueryOptions).catch(noop)
    context.queryClient.query(context.sessionAppQueryOptions).catch(noop)
    context.queryClient.query(context.myEmployersQueryOptions).catch(noop)

    // `theme` is resolved server-side from the cookie so SSR paints the `dark` class directly (no light→dark flash), and
    // dehydrated so hydration reads the same value. `locale` comes from the router context, resolved and activated once
    // in `getRouter`, so there is no second resolve/catalog-load here.
    return { theme: resolveServerTheme(), locale: context.locale }
  },
  head: () => ({
    meta: [
      { charSet: 'utf-8' },
      { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
      { name: 'theme-color', content: '#33CCAA' },
      { title: 'D’Anchiano' },
      { name: 'description', content: 'La manera más fácil y rápida de evaluar talento' },
    ],
    // app.css and the font preload are deliberately NOT here: a stylesheet emitted via head() gets a `precedence` prop,
    // and React 19 hoists those above our inline `manageTheme` script. A parser-blocking script after a render-blocking
    // stylesheet waits for it, and the browser paints the themeless DOM meanwhile → light→dark flash in system mode.
    // As plain links in JSX they keep DOM order, so the script runs first.
    links: [{ rel: 'shortcut icon', href: assetUrl('favicon.ico') }],
    // A separate entry, and a plain constant: it arms a MutationObserver that must run before the parser delivers the
    // body, so it can't wait for the deferred entry module. Its text must be byte-identical on both sides or the whole
    // head bootstrap re-executes on hydration.
    scripts: [{ children: initGlobalsScript }, { children: HYDRATION_REWRITE_MONITOR_SCRIPT }],
  }),
  component: RootComponent,
  // Renders INSIDE RootComponent's already-mounted shell, so it must emit only the not-found content: its own
  // <RootDocument> nested a second <html>/<head>/<body>, which browsers strip while parsing → React #418. See #47.
  notFoundComponent: () => {
    return <p>{'Not found :('}</p>
  },
})

// Dev-only pointer-inspection overlay. A module-level helper, not an inline import() in the effect: the React
// Compiler bails out of a whole component containing an import(). Off under VITE_E2E, where the overlay would
// intercept pointer events on full-page views and inside portals.
function loadReactGrabDevOverlay() {
  if (import.meta.env.DEV && !import.meta.env.VITE_E2E) {
    void import('react-grab')
  }
}

function RootComponent() {
  const { theme, locale } = Route.useLoaderData()
  // Inside a route-transition live preview the document shell already exists in the real tree the preview renders
  // inside, so redrawing it would nest a second document in the pane. See route-transition/route-preview.tsx.
  const { previewFrom } = Route.useRouteContext()

  React.useEffect(() => {
    loadReactGrabDevOverlay()
  }, [])

  // Stamped in an effect, so it appears only once the client tree has committed and React's root event delegation is
  // live — until then a click on streamed SSR markup is silently dropped. The e2e harness gates navigation on it.
  React.useEffect(() => {
    document.documentElement.dataset.hydrated = 'true'
  }, [])

  if (previewFrom !== undefined) return <Outlet />

  return (
    <RootDocument theme={theme} locale={locale}>
      <AppShell>
        <Outlet />
      </AppShell>
    </RootDocument>
  )
}

function RootDocument(
  props: Readonly<{ children: React.ReactNode; theme?: 'dark' | 'light' | null; locale?: string }>,
) {
  // Resolved per render so SSR (process.env.URL_CDN) and the client's first render (window.__CDN_URL) agree byte-for-byte.
  const cdnUrl = getRuntimeCdnUrl()
  const cdnOrigin = cdnUrl.startsWith('http') ? new URL(cdnUrl).origin : ''
  const fontUrl = assetUrl('montserrat-latin-wght-normal.woff2')
  // Declared inline in the document <head>, co-located with the preload and NOT in app.css, so the browser matches the
  // @font-face to the preloaded woff2 at initial parse — otherwise Chrome warns the preload is unused and text falls
  // back to the metric-matched Verdana.
  const fontFaceStyle = `@font-face{font-family:'Montserrat';font-style:normal;font-display:swap;font-weight:100 900;src:url('${fontUrl}') format('woff2')}`
  return (
    // In system mode `theme` is null (the server can't read the OS preference) and the inline manageTheme script adds
    // `dark` before paint; suppressHydrationWarning lets that script-set class stand through hydration. The script and
    // the AppShell effect stay the live source of truth for system mode, OS changes and toggles.
    <html lang={props.locale} className={props.theme === 'dark' ? 'dark' : undefined} suppressHydrationWarning>
      <head>
        {/* Warm up the cross-origin CDN connection (DNS+TCP+TLS) before the parser reaches the asset
            URLs, so the handshake overlaps HTML parsing instead of blocking the first chunk/font.
            crossOrigin matches the anonymous CORS fetches the CDN serves (modules + font). Skipped
            when assets are same-origin (dev), where CDN_ORIGIN is ''. */}
        {cdnOrigin ? (
          <>
            <link rel="preconnect" href={cdnOrigin} crossOrigin="anonymous" />
            <link rel="dns-prefetch" href={cdnOrigin} />
          </>
        ) : null}
        {/* manageTheme MUST run before the stylesheet. It sets the `dark` class from the cookie /
            `prefers-color-scheme` so the first paint is correct even in system mode (no `theme`
            cookie → the server can't pre-paint the class on <html>). */}
        <script dangerouslySetInnerHTML={{ __html: manageThemeScript }} />
        {/* Plain (non-precedence) <link> so React 19 keeps it HERE in DOM order — after the script
            — instead of hoisting it above (which it does for precedence stylesheets emitted via
            head().links). Still render-blocking, so no flash of unstyled content. See head() note. */}
        <link rel="stylesheet" href={appCss} />
        <link rel="preload" href={fontUrl} as="font" type="font/woff2" crossOrigin="anonymous" />
        <style dangerouslySetInnerHTML={{ __html: fontFaceStyle }} />
        <HeadContent />
      </head>
      <body>
        {props.children}
        <Scripts />
      </body>
    </html>
  )
}

// Without this, FieldLabelRow falls back to its hardcoded English "Optional" in every locale.
function OptionalLabelProvider(props: Readonly<{ children: React.ReactNode }>) {
  const { t } = useLingui()
  return <FormOptionalLabelProvider value={t`Optional`}>{props.children}</FormOptionalLabelProvider>
}

// The config comes from its OWN `getApp` query, keyed on the request's appId rather than the user, so an embed/API
// visitor gets the client's branding even when logged out. `app` is undefined outside an app/embed context.
function AppConfigSync() {
  const { data: app } = useSuspenseQuery(Route.useRouteContext().sessionAppQueryOptions)
  const setStore = useAppStore((state) => state.setStore)

  React.useEffect(() => {
    if (!app) return
    setStore({
      app,
      // The per-app theme wins in app/SDK context so a client can pin its embed; `null` leaves the visitor's own preference.
      ...(typeof app.darkMode === 'boolean' ? { darkMode: app.darkMode ? 'dark' : 'light' } : {}),
      customize: {
        accentColor: app.accentColor ?? null,
        darkMode: app.darkMode ?? undefined,
        applicantButtonExit: app.applicantButtonExit ?? undefined,
        jobButtonExit: app.jobButtonExit ?? undefined,
        enableJobAddApplicantButton: app.enableJobAddApplicantButton,
      },
    })
  }, [app, setStore])

  return null
}

function AppUserSync() {
  const user = useSessionUser()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const routeTo = useAppStore((state) => state.store.routeTo)
  const profiles = useAppStore((state) => state.store.profiles)

  // Identity guard for the persisted (localStorage) query cache — see PERSISTED_IDENTITY_KEY in @/main, and
  // persistedCacheIdentity for what counts as one. Social OAuth and every interface/employer/role change land via a
  // document load that rehydrates the PREVIOUS context's blob (only password login / logout clear it), so a query
  // whose scope the server reads off the session surfaced for the wrong one. Gated on OWNS_PERSISTED_CACHE: a
  // token-scoped document never
  // restores the blob, and stamping the tag with the token entity's id would purge the OWNER's cache on their next load.
  React.useEffect(() => {
    if (!OWNS_PERSISTED_CACHE) return
    const currentId = persistedCacheIdentity(user)
    const previousId = window.localStorage.getItem(PERSISTED_IDENTITY_KEY)
    if (previousId !== null && previousId !== currentId) {
      clearQueryClient()
      queryClient.setQueryData(trpc.auth.getUser.queryKey(), user)
    }
    window.localStorage.setItem(PERSISTED_IDENTITY_KEY, currentId)
  }, [user, queryClient])

  React.useEffect(() => {
    if (routeTo) {
      // TODO: maintaining retrocompatibility with old sdk
      // TODO: Fix this, surely is not working correctly now with the new router
      navigate({
        to: routeTo,
        search: {
          ...profiles,
          second:
            profiles?.first && !profiles.second && user?.type === 'applicant'
              ? { type: 'applicant', id: user.id }
              : profiles?.second,
        },
        replace: true,
      })
    }
  }, [user, routeTo, profiles, navigate])

  return null
}

// A rendered <style>, not a post-mount effect: the per-app color comes from the SSR-prefetched `getApp`, so the rule
// is in the server HTML and the accent paints with the first frame — no teal→custom flash, and it paints for a
// logged-out embed visitor too. A live SDK `customize` postMessage overrides it via the store.
// `:root:root` wins over app.css's `:root`/`.dark`, and it is emitted after the app.css <link> for equal-specificity ties.
function AccentTheme() {
  const { data: app } = useSuspenseQuery(Route.useRouteContext().sessionAppQueryOptions)
  const overrideAccent = useAppStore((state) => state.store.customize.accentColor)
  const accent = overrideAccent ?? app?.accentColor ?? null
  const accentVars = Object.entries(accentThemeVars(accent))
    .map(([key, value]) => `${key}:${value}`)
    .join(';')
  const css = `:root:root{${accentVars}}`
  return <style>{css}</style>
}

function AppShell(props: Readonly<{ children: React.ReactNode }>) {
  const queryClient = useQueryClient()
  // Request-scoped instance from router context — never the shared global — so concurrent SSR renders can't clobber
  // this request's locale (React #418).
  const { i18n } = Route.useRouteContext()
  /**
   * SDK
   * - run renders and requests from parent
   * - send response for requests made from parent
   * - recover last render props after app autoupdate (see Page props implementation)
   */
  const sdk = typeof window !== 'undefined' && window.self !== window.top
  const setStore = useAppStore((state) => state.setStore)
  const clientId = useAppStore((state) => state.store.clientId)
  const locale = useAppStore((state) => state.store.locale)
  const darkMode = useAppStore((state) => state.store.darkMode)
  const app = useAppStore((state) => state.store.app)

  React.useEffect(() => {
    const listener = (
      event: MessageEvent<{
        type: string
        props?: Record<string, unknown>
        url?: string
        params?: unknown
        key?: string
      }>,
    ) => {
      if (event.data.type === 'danchiano_render') {
        setStore({
          ...event.data.props,
          sdk,
          callback: (value) => {
            if (!sdk) return
            window.parent.postMessage({ type: 'danchiano_callback', result: value }, '*')
          },
        })
      } else if (event.data.type === 'danchiano_request') {
        if (!event.data.url || !event.data.params) return

        // TODO: check if we need all of this flow, we should add headers and everything here
        fetch(event.data.url, event.data.params)
          .then((result) => {
            window.parent.postMessage(
              {
                type: 'danchiano_response',
                key: event.data.key,
                result,
              },
              '*',
            )
          })
          .catch((error: unknown) => {
            window.parent.postMessage(
              {
                type: 'danchiano_response',
                key: event.data.key,
                result: (error as { response?: string }).response ?? error,
              },
              '*',
            )
          })
      }
    }

    window.addEventListener('message', listener, false)
    window.parent.postMessage({ type: 'danchiano_loaded' }, '*')
    return () => window.removeEventListener('message', listener, false)
  }, [sdk, setStore])

  React.useEffect(() => {
    if (!sdk) return

    const target = document.getElementById('root-page')
    if (target) {
      const sendHeight = () => {
        window.parent.postMessage(
          {
            type: 'danchiano_resize',
            height: `${target.offsetHeight}px`,
          },
          '*',
        )
      }
      sendHeight()
      const observer = new ResizeObserver(sendHeight)
      observer.observe(target)
    }
  }, [sdk])

  const setHydrated = useAppStore((state) => state.setHydrated)
  React.useEffect(() => {
    // SSR'd interactive controls read this (via useHydrated) to stay disabled until handlers are attached, so
    // pre-hydration clicks aren't silently dropped.
    setHydrated()
  }, [setHydrated])

  React.useEffect(() => {
    // Keeps assistive tech announcing content in the right language; the SSR value comes from the loader.
    if (locale) document.documentElement.lang = locale
  }, [locale])

  const prevScope = React.useRef<string | null>(null)
  React.useEffect(() => {
    // Only on an actual VALUE change: this effect re-runs with the same clientId/locale (e.g. after getUser resolves),
    // and invalidating then throws away the fresh SSR-hydrated cache, so a "skip first run" flag isn't enough.
    const scope = `${clientId ?? ''}|${locale ?? ''}`
    if (prevScope.current !== null && prevScope.current !== scope) {
      queryClient.invalidateQueries()
    }
    prevScope.current = scope
  }, [clientId, locale, queryClient])

  // The visitor's `theme` cookie is their PERSONAL standalone preference. In any embed/API context — the SDK iframe,
  // or an API token-link app (full page, so `sdk` is false but `app` is set) — the client's config dictates the mode.
  const isEmbedded = sdk || !!app

  React.useEffect(() => {
    if (darkMode === 'dark') {
      if (!isEmbedded) setCookie('theme', 'dark')

      document.documentElement.classList.add('dark')
      setStore({ resolvedDarkMode: 'dark' })
    } else if (darkMode === 'light') {
      if (!isEmbedded) setCookie('theme', 'light')

      document.documentElement.classList.remove('dark')
      setStore({ resolvedDarkMode: 'light' })
    } else {
      if (!isEmbedded) deleteCookie('theme')

      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        document.documentElement.classList.add('dark')
        setStore({ resolvedDarkMode: 'dark' })
      } else {
        document.documentElement.classList.remove('dark')
        setStore({ resolvedDarkMode: 'light' })
      }
    }
  }, [darkMode, isEmbedded, setStore])

  // Deliberately never auto-reloads: mid-deployment the flag flaps between old and new servers, which would reload
  // the page repeatedly under the user.

  return (
    <div id="root-page" className="relative mx-auto">
      <I18nProvider i18n={i18n}>
        <TooltipProvider delay={30}>
          <OptionalLabelProvider>
            <React.Suspense fallback={null}>
              <AppConfigSync />
              <AppUserSync />
              <AccentTheme />
            </React.Suspense>
            {props.children}
            <ClientOnly fallback={null}>
              <CookieBanner />
            </ClientOnly>
          </OptionalLabelProvider>
        </TooltipProvider>
        <AppToaster />
      </I18nProvider>

      {/* Off under VITE_E2E: its server vite plugin (the console-pipe) is disabled in
          the e2e stack, so the panel's /__tsd/* calls would 404; and it's pure dev UI
          irrelevant to headless tests. */}
      {import.meta.env.DEV && !import.meta.env.VITE_E2E && (
        <ClientOnly fallback={null}>
          <TanStackDevtools
            plugins={[
              {
                name: 'Tanstack Router',
                render: <TanStackRouterDevtoolsPanel />,
              },
              {
                name: 'Tanstack Query',
                render: <ReactQueryDevtoolsPanel />,
              },
            ]}
          />
        </ClientOnly>
      )}
    </div>
  )
}
